import { Badge, PageHeader, SuspenseLoader, TabsMenu } from '@folio/components';
import { buttonVariants } from '@folio/components/ui/button';
import {
  ClaimStatusBadge,
  claimInsuredName,
  claimTypeLabel,
} from '@folio/components/module-elements/ClaimStatusBadge';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@folio/components/ui/breadcrumb';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';
import { useAssertedParams } from '@folio/lib';
import { formatDate } from '@folio/lib/date-formatting';
import { useQuery } from '@tanstack/react-query';
import {
  claimManagementControllerGetQueryOptions,
  policyControllerGetPolicyQueryOptions,
} from 'korint-sdk';
import { CalendarIcon, FileTextIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet } from 'react-router-dom';

export function ClaimDetailsLayout() {
  const { claimId } = useAssertedParams<{ claimId: string }>();
  const { t, i18n } = useTranslation();

  const { data: claim } = useQuery(claimManagementControllerGetQueryOptions(claimId));
  // Absent when the caller can't read policies: the contract parts then stay hidden
  const { data: policy } = useQuery({
    ...policyControllerGetPolicyQueryOptions(claim?.policyId),
    enabled: !!claim?.policyId,
    retry: false,
  });
  const insuredName = claim && claimInsuredName(claim);
  const occurredAt = claim?.incident.occurredAt;

  return (
    <Stack>
      <Stack gap={2}>
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/claims">{t('claimDetails.breadcrumb')}</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="break-all">{claimId}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <PageHeader
          className="mb-0"
          title={
            <span className="break-all">
              {insuredName ? `${claimId} — ${insuredName}` : claimId}
            </span>
          }
          subtitle={
            claim && (
              <Group gap={2} wrap>
                <Badge variant="outline">{claimTypeLabel(claim)}</Badge>
                <ClaimStatusBadge claim={claim} />
                {policy?.externalId && (
                  <Link to={`/policies/${policy.id}`} className="text-sm hover:underline">
                    {t('claimDetails.policy')}{' '}
                    <span className="font-medium text-primary">{policy.externalId}</span>
                  </Link>
                )}
                {occurredAt && (
                  <Group gap={1}>
                    <CalendarIcon className="size-4 text-muted-foreground" />
                    <Text inline size="sm">
                      {t('claimDetails.occurredOn', {
                        date: formatDate(new Date(occurredAt), i18n.language),
                      })}
                    </Text>
                  </Group>
                )}
              </Group>
            )
          }
          right={
            policy && (
              <Link
                to={`/policies/${policy.id}`}
                className={buttonVariants({ variant: 'outline' })}
              >
                <FileTextIcon className="mr-2 size-4" />
                {t('claimDetails.seePolicy')}
              </Link>
            )
          }
        />
      </Stack>
      <TabsMenu
        tabs={[
          { label: t('claimDetails.tabs.overview'), href: `/claims/${claimId}` },
          { label: t('claimDetails.tabs.information'), href: `/claims/${claimId}/information` },
          { label: t('claimDetails.tabs.parties'), href: `/claims/${claimId}/parties` },
          { label: t('claimDetails.tabs.documents'), href: `/claims/${claimId}/documents` },
        ]}
      />
      <SuspenseLoader>
        <Outlet />
      </SuspenseLoader>
    </Stack>
  );
}
